import { CurrencyType, PaymentTypes, UserEventStatus } from "../../../../common/enums";
import { IStudioSettingsModelPaymentType } from "../../../../common/models/IStudioSettingsModel";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";

// Never picked by the one-tap arrival: a plan is used only through the client's own card,
// the deposit and the gift voucher need their own ledger, Multisport and CoolFit their own
// pricing, and Stripe is money taken online (no charge stands behind a tap, and undoing an
// arrival keeps a Stripe payment, so the row would look prepaid). They stay on the arrival page.
const EXCLUDED_PAYMENT_TYPES: number[] = [
  PaymentTypes.Plan,
  PaymentTypes.Deposit,
  PaymentTypes.GiftVoucher,
  PaymentTypes.Multisport,
  PaymentTypes.CoolFit,
  PaymentTypes.Stripe,
];

/**
 * A reservation whose arrival still needs a payment (confirmed but unpaid, or cancelled
 * with a fee): the row offers the arrival page, and the one-tap arrival next to it.
 */
export const isAwaitingArrivalPayment = (item: IPlannerEventUserModel) =>
  ((item.status === UserEventStatus.Confirmed &&
    (item.paymentType === undefined ||
      !item.pricePaidInEur ||
      item.pricePaidInEur <= 0)) ||
    item.status === UserEventStatus.PenaltyCancelled) &&
  !item.isCancelled;

/**
 * Rows that get the one-tap arrival: confirmed and not paid yet. Not a reservation cancelled
 * with a fee: undoing its arrival would leave it Confirmed, holding a place again, so it keeps
 * the arrival page, where "Платена без присъствие" can be chosen.
 */
export const canQuickArrive = (item: IPlannerEventUserModel) =>
  item.status === UserEventStatus.Confirmed && isAwaitingArrivalPayment(item);

/** The studio's first payment type (by its order) that the one-tap arrival may use. */
export const pickQuickArrivalPaymentType = (
  paymentTypes?: IStudioSettingsModelPaymentType[],
): PaymentTypes | undefined => {
  const first = [...(paymentTypes ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .find((x) => !EXCLUDED_PAYMENT_TYPES.includes(x.paymentType));

  return first?.paymentType as PaymentTypes | undefined;
};

/**
 * The query of PUT Planner/MarkUserAsArrived for a one-tap arrival: the client's plan for
 * this class when they have one and the studio takes plans as a payment type, otherwise the
 * studio's first payment type at the class price. Undefined when neither is possible.
 */
export const buildQuickArrivalRequest = (
  user: IPlannerEventUserModel,
  eventId: number,
  eventPrice: number,
  paymentTypes?: IStudioSettingsModelPaymentType[],
) => {
  // A studio that doesn't list "Абонаментна карта" among its payment types can't take a plan
  // at arrival: the backend refuses it (10409) and the arrival page doesn't offer it.
  const studioTakesPlans = (paymentTypes ?? []).some(
    (x) => x.paymentType === PaymentTypes.Plan,
  );
  const plan =
    studioTakesPlans && user.currentPlan && !user.currentPlan.isExpired
      ? user.currentPlan
      : undefined;
  const paymentTypeId = plan
    ? PaymentTypes.Plan
    : pickQuickArrivalPaymentType(paymentTypes);

  if (!paymentTypeId) {
    return undefined;
  }

  return {
    eventId,
    userId: user.id,
    paymentTypeId,
    subscriptionId: plan?.subscriptionId ?? 0,
    price: eventPrice ?? 0,
    currencyId: CurrencyType.EUR,
    onlyPaid: false,
  };
};
